import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ethers } from "ethers";
import Web3Modal from "web3modal";
import WalletConnectProvider from "@walletconnect/web3-provider";

import { getContract } from '../web3/contract';
import { AppThunk } from '../store';
import BigNumber from 'bignumber.js';

interface MintState {
  mintPending: boolean;
  transactionHash?: string;
  pendingWalletConfirm: boolean;
  connected: boolean;
  ethNetwork: string;
}

const initialState: MintState = {
  mintPending: false,
  pendingWalletConfirm: false,
  connected: false,
  ethNetwork: process.env.GATSBY_ETH_NETWORK_ID as string,
}

const service = createSlice({
  name: 'nft',
  initialState,
  reducers: {
    setEthNetwork(state, action: PayloadAction<string>): MintState {
      return { ...state, ethNetwork: action.payload };
    },
    setConnected(state, action: PayloadAction<boolean>): MintState {
      return { ...state, connected: action.payload };
    },
    setPendingWalletConfirm(state, action: PayloadAction<boolean>): MintState {
      return { ...state, pendingWalletConfirm: action.payload };
    },
    setMintPending(state, action: PayloadAction<{ mintPending: boolean, transactionHash: string }>): MintState {
      return {
        ...state,
        ...action.payload,
      };
    },
  }
});

let provider: ethers.providers.Web3Provider;



export interface ConnectWalletArgs {
  onDone: (result: boolean) => void;
}

const connectWallet = (args: ConnectWalletArgs): AppThunk => async (dispatch, getState) => {
  try {
    const state = getState();
    if (state.mint.connected || Boolean(provider)) {
      args.onDone(true);
      return;
    }

    const providerOptions = {
      walletconnect: {
        package: WalletConnectProvider, // required
        options: {
          infuraId: process.env.GATSBY_INFURA_ID // required
        }
      }
    };

    const web3Modal = new Web3Modal({
      theme: 'dark',
      network: process.env.GATSBY_ETH_NETWORK,
      cacheProvider: false,
      providerOptions,
    });


    web3Modal.clearCachedProvider();



    const instance = await web3Modal.connect();
    dispatch(service.actions.setConnected(true));

    provider = new ethers.providers.Web3Provider(instance);

    // Subscribe to accounts change
    instance.on("accountsChanged", (accounts: string[]) => {
      console.log('accountsChanged', accounts);
    });

    // Subscribe to chainId change
    instance.on("chainChanged", async (chainId: number) => {
      console.log('chainChanged', chainId);
      dispatch(service.actions.setEthNetwork(chainId.toString()));
    });

    // Subscribe to provider connection
    instance.on("connect", (info: { chainId: number }) => {
      dispatch(service.actions.setConnected(true));
      console.log('connect', info);
    });

    // Subscribe to provider disconnection
    instance.on("disconnect", (error: { code: number; message: string }) => {
      dispatch(service.actions.setConnected(false));
      console.log('disconnect', error);
    });
    args.onDone(true);
  }
  catch (e) {
    console.error(e);
  }
}

interface MintTokensArgs {
  count: number;
  price: BigNumber;
  onMintStarted: () => void;
}

const mintTokens = ({count, price, onMintStarted}: MintTokensArgs): AppThunk => async (dispatch, getState) => {
  try {
    const state = getState();
    if (!state.mint.connected || !Boolean(provider)) {
      console.error('not connected...');
      return;
    }

    const contract = await getContract();


    const signer = provider.getSigner();
    const address = await signer.getAddress();
    const contractWithSigner = contract.connect(signer);
    dispatch(service.actions.setPendingWalletConfirm(true));
    const transaction = await contractWithSigner.mintTo(address, count, {
      value: ethers.utils.parseEther(price.toString())
    });
    console.log('transaction', transaction);
    dispatch(service.actions.setPendingWalletConfirm(false));
    dispatch(service.actions.setMintPending({
      mintPending: true,
      transactionHash: transaction.hash,
    }));
    onMintStarted();
    await transaction.wait();
    dispatch(service.actions.setMintPending({
      mintPending: false,
      transactionHash: transaction.hash,
    }));
  }
  catch (e) {
    dispatch(service.actions.setPendingWalletConfirm(false));
    console.error(e);
  }
}



export const actions = {
  connectWallet,
  mintTokens,
}

export default service.reducer;